import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { InputState, SelectInput } from '@wix/cli-ui-kit';
import type { SelectOption } from '@wix/cli-ui-kit';
import type { VariantOf } from 'variant';
import { match, variant } from 'variant';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { useGenerateCustomElement } from '../custom-element/index.js';
import { useGenerateReactComponent } from '../react-component/index.js';
import { useWizardStep } from '../Wizard.js';

const SiteWidgetFrameworks = variant({
  CustomElement: {},
  ReactComponent: {},
});

type Frameworks = VariantOf<typeof SiteWidgetFrameworks>;

const FrameworkSelector: FC<{
  onSubmit: (framework: SelectOption<Frameworks>) => void;
  framework: SelectOption<Frameworks>;
  frameworks: Array<SelectOption<Frameworks>>;
}> = ({ onSubmit, framework, frameworks }) => {
  const bi = useWixCliAstroBi();
  const { goToNextStep, isActive } = useWizardStep();

  const selectFramework = useCallback(
    (selectedFramework: SelectOption<Frameworks>) => {
      bi.cliFlowStepAnswered({
        question: 'Which framework do you want to use?',
        questionKey: 'generate_command.site_widget.choose_framework',
        answer: selectedFramework.title,
      });
      onSubmit(selectedFramework);
      goToNextStep();
    },
    [bi, onSubmit, goToNextStep]
  );

  return (
    <SelectInput<Frameworks>
      value={
        !isActive
          ? InputState.Submitted(framework)
          : InputState.NotSubmitted(framework)
      }
      label={'Which framework do you want to use?'}
      options={frameworks}
      onSubmit={selectFramework}
    />
  );
};

export const useGenerateSiteWidget = () => {
  const generateCustomElementSteps = useGenerateCustomElement();
  const generateReactComponentSteps = useGenerateReactComponent();

  const frameworks: Array<SelectOption<Frameworks>> = useMemo(
    () => [
      {
        title: 'React Component',
        key: 'react-component',
        value: SiteWidgetFrameworks.ReactComponent(),
        description:
          'a new way to add React components to the Wix editors and site (read more)',
      },
      {
        title: 'Custom Element',
        key: 'custom-element',
        value: SiteWidgetFrameworks.CustomElement(),
      },
    ],
    []
  );

  const [framework, setFramework] = useState<SelectOption<Frameworks>>(
    frameworks[0]!
  );

  return [
    <FrameworkSelector
      frameworks={frameworks}
      framework={framework}
      onSubmit={setFramework}
    />,
    ...match(framework.value, {
      ReactComponent: () => generateReactComponentSteps,
      CustomElement: () => generateCustomElementSteps,
      default: () => [],
    }),
  ];
};
